import { Chain as EvmChain } from "viem";
import {
  AssetType,
  Chain,
  ChainID,
  ChainName,
  ChainState,
  ChainType,
  IndexerTicketStatus,
  ServiceType,
  TicketAction,
} from "@types";
import {
  bevm,
  bitlayer,
  bsquared,
  bitfinity,
  ailayer,
} from "@wallet-kits/evm-wallet-kit/networks";
import {
  AILayer,
  Base,
  Bevm,
  BitLayer,
  Bitcoin,
  Bitfinity,
  Bob,
  Bsquared,
  Core,
  Doge,
  Ethereum,
  ICP,
  Merlin,
  Osmosis,
  Rootstock,
  Solana,
  Sui,
  Ton,
  XLayer,
} from "../components/icons";
import React from "react";
import {
  rootstock,
  bob,
  merlin,
  xLayer,
  mainnet,
  coreDao,
  base,
} from "viem/chains";

export const TOAST_ERROR_DURATION = 8000;

export const FETCH_TOKEN_INTERVAL = 3 * 60 * 1000;

export const CHECK_TICKET_STATUS_INTERVAL = 10000;

export const CHECK_TX_STATUS_INTERVAL = 3000;

export const FETCH_BTC_FEE_RATE_INTERVAL = 60000;

export const ICP_DECIMALS = 8;
export const BTC_DECIMALS = 8;

export const BTC_ICON = "https://hub.omnity.network/bitcoin.svg";

export const LEDGER_CANISTER_ID = "ryjl3-tyaaa-aaaaa-aaaba-cai";

export const DEFAULT_TOKEN_BY_ASSET = {
  [AssetType.runes]: "Bitcoin-runes-DOG•GO•TO•THE•MOON",
  [AssetType.icrc]: "sICP-native-ICP",
  [AssetType.ckbtc]: "sICP-icrc-ckBTC",
  [AssetType.doge]: "dogecoin-native-DOGE",
  [AssetType.spl]: "Solana-native-SOL",
};

export const ADD_RUNES_SUFFIX = ".OT";

export const BADGE_COLORS = {
  [TicketAction.Mint]: "green",
  [TicketAction.Burn]: "red",
  [TicketAction.Transfer]: "blue",
  [TicketAction.Redeem]: "teal",
};

export const TICKET_STATUS_COLORS = {
  [IndexerTicketStatus.Generating]: "gray.400",
  [IndexerTicketStatus.Unknown]: "pink.400",
  [IndexerTicketStatus.Pending]: "pink.400",
  [IndexerTicketStatus.WaitingForConfirmBySrc]: "orange.400",
  [IndexerTicketStatus.WaitingForConfirmByDest]: "teal.400",
  [IndexerTicketStatus.Finalized]: "green.400",
  [IndexerTicketStatus.Failed]: "red.400",
};

export const CHAIN_LIST: {
  [key in ChainID]: {
    chainName: ChainName;
    evmChain?: EvmChain;
    serviceType: ServiceType;
    icon: React.ElementType;
    assets: AssetType[];
    chainState?: ChainState;
  };
} = {
  [ChainID.Bitcoin]: {
    chainName: ChainName.Bitcoin,
    serviceType: ServiceType.Customs,
    icon: Bitcoin,
    assets: [AssetType.runes],
  },
  [ChainID.eICP]: {
    chainName: ChainName.ICP,
    serviceType: ServiceType.Route,
    icon: ICP,
    assets: [AssetType.runes],
  },
  [ChainID.Ethereum]: {
    chainName: ChainName.Ethereum,
    serviceType: ServiceType.Route,
    evmChain: {
      ...mainnet,
      rpcUrls: {
        default: {
          http: ["https://eth.drpc.org", "https://cloudflare-eth.com"],
        },
      },
    },
    icon: Ethereum,
    assets: [AssetType.runes, AssetType.icrc],
  },
  [ChainID.BitLayer]: {
    chainName: ChainName.BitLayer,
    serviceType: ServiceType.Route,
    evmChain: bitlayer,
    icon: BitLayer,
    assets: [AssetType.runes],
  },
  [ChainID.Bitfinity]: {
    chainName: ChainName.Bitfinity,
    serviceType: ServiceType.Route,
    evmChain: bitfinity,
    icon: Bitfinity,
    assets: [AssetType.runes, AssetType.icrc],
  },
  [ChainID.BEVM]: {
    chainName: ChainName.BEVM,
    serviceType: ServiceType.Route,
    evmChain: bevm,
    icon: Bevm,
    assets: [AssetType.runes],
    chainState: ChainState.Deactive,
  },
  [ChainID.BSquared]: {
    chainName: ChainName.BSquared,
    serviceType: ServiceType.Route,
    evmChain: bsquared,
    icon: Bsquared,
    assets: [AssetType.runes],
    chainState: ChainState.Deactive,
  },
  [ChainID.XLayer]: {
    chainName: ChainName.XLayer,
    serviceType: ServiceType.Route,
    evmChain: xLayer,
    icon: XLayer,
    assets: [AssetType.runes],
    chainState: ChainState.Deactive,
  },
  [ChainID.Merlin]: {
    chainName: ChainName.Merlin,
    serviceType: ServiceType.Route,
    evmChain: merlin,
    icon: Merlin,
    assets: [AssetType.runes],
    chainState: ChainState.Deactive,
  },
  [ChainID.Bob]: {
    chainName: ChainName.Bob,
    serviceType: ServiceType.Route,
    evmChain: bob,
    icon: Bob,
    assets: [AssetType.runes],
    chainState: ChainState.Deactive,
  },
  [ChainID.RootStock]: {
    chainName: ChainName.Rootstock,
    serviceType: ServiceType.Route,
    evmChain: rootstock,
    icon: Rootstock,
    assets: [AssetType.runes],
    chainState: ChainState.Deactive,
  },
  [ChainID.AILayer]: {
    chainName: ChainName.AILayer,
    serviceType: ServiceType.Route,
    evmChain: ailayer,
    icon: AILayer,
    assets: [AssetType.runes],
    chainState: ChainState.Deactive,
  },
  [ChainID.sICP]: {
    chainName: ChainName.ICP,
    serviceType: ServiceType.Customs,
    icon: ICP,
    assets: [AssetType.icrc],
  },
  [ChainID.eSolana]: {
    chainName: ChainName.Solana,
    serviceType: ServiceType.Route,
    icon: Solana,
    assets: [AssetType.runes, AssetType.icrc],
  },
  [ChainID.sSolana]: {
    chainName: ChainName.Solana,
    serviceType: ServiceType.Customs,
    icon: Solana,
    assets: [AssetType.spl],
  },
  [ChainID.Core]: {
    chainName: ChainName.Core,
    serviceType: ServiceType.Route,
    evmChain: coreDao,
    icon: Core,
    assets: [AssetType.runes, AssetType.ckbtc],
  },
  [ChainID.Osmosis]: {
    chainName: ChainName.Osmosis,
    serviceType: ServiceType.Route,
    icon: Osmosis,
    assets: [AssetType.ckbtc, AssetType.icrc, AssetType.doge],
  },
  [ChainID.BitcoinckBTC]: {
    chainName: ChainName.Bitcoin,
    serviceType: ServiceType.Customs,
    icon: Bitcoin,
    assets: [AssetType.ckbtc],
  },
  [ChainID.Ton]: {
    chainName: ChainName.Ton,
    serviceType: ServiceType.Route,
    icon: Ton,
    assets: [AssetType.runes, AssetType.ckbtc, AssetType.icrc],
  },
  [ChainID.Doge]: {
    chainName: ChainName.Doge,
    serviceType: ServiceType.Customs,
    icon: Doge,
    assets: [AssetType.doge],
  },
  [ChainID.Sui]: {
    chainName: ChainName.Sui,
    serviceType: ServiceType.Route,
    icon: Sui,
    assets: [AssetType.ckbtc, AssetType.icrc],
  },
  [ChainID.Base]: {
    chainName: ChainName.Base,
    serviceType: ServiceType.Route,
    icon: Base,
    evmChain: base,
    assets: [AssetType.runes, AssetType.icrc],
  },
};

export const CKBTC_CHAIN_LIST: Chain[] = [
  {
    chain_id: ChainID.BitcoinckBTC,
    chain_name: ChainName.Bitcoin,
    canister_id: "",
    fee_token: [],
    chain_state: ChainState.Active,
    chain_type: ChainType.Settlement,
    counterparties: [ChainID.Osmosis, ChainID.Core, ChainID.Ton],
    service_type: ServiceType.Customs,
    contract_address: "",
    asset_type: AssetType.ckbtc,
  },
  {
    chain_id: ChainID.Osmosis,
    chain_name: ChainName.Osmosis,
    canister_id: "ystyg-kaaaa-aaaar-qaieq-cai",
    fee_token: [],
    chain_state: ChainState.Active,
    chain_type: ChainType.ExecutionChain,
    counterparties: [ChainID.BitcoinckBTC],
    service_type: ServiceType.Route,
    contract_address:
      "osmo10c4y9csfs8q7mtvfg4p9gd8d0acx0hpc2mte9xqzthd7rd3348tsfhaesm",
    asset_type: AssetType.ckbtc,
  },
  {
    chain_id: ChainID.Core,
    chain_name: ChainName.Core,
    canister_id: "vopxt-5qaaa-aaaar-qajnq-cai",
    fee_token: [],
    chain_state: ChainState.Active,
    chain_type: ChainType.ExecutionChain,
    counterparties: [ChainID.BitcoinckBTC],
    service_type: ServiceType.Route,
    asset_type: AssetType.ckbtc,
    evm_chain: coreDao,
    contract_address: "0x1Ad8cec9E5a4A441FE407785E188AbDeb4371468",
  },
  {
    chain_id: ChainID.Ton,
    chain_name: ChainName.Ton,
    canister_id: "p5ykc-qaaaa-aaaar-qalyq-cai",
    fee_token: [],
    chain_state: ChainState.Active,
    chain_type: ChainType.ExecutionChain,
    counterparties: [ChainID.BitcoinckBTC],
    service_type: ServiceType.Route,
    asset_type: AssetType.ckbtc,
  },
];

export const DOGECOIN_CHAIN_LIST: Chain[] = [
  {
    chain_id: ChainID.Doge,
    chain_name: ChainName.Doge,
    canister_id: "nnqqa-aaaaa-aaaar-qamla-cai",
    fee_token: [],
    chain_state: ChainState.Active,
    chain_type: ChainType.Settlement,
    counterparties: [ChainID.Osmosis],
    service_type: ServiceType.Customs,
    contract_address: "",
    asset_type: AssetType.doge,
  },
  {
    chain_id: ChainID.Osmosis,
    chain_name: ChainName.Osmosis,
    canister_id: "ystyg-kaaaa-aaaar-qaieq-cai",
    fee_token: [],
    chain_state: ChainState.Active,
    chain_type: ChainType.ExecutionChain,
    counterparties: [ChainID.Doge],
    service_type: ServiceType.Route,
    contract_address:
      "osmo10c4y9csfs8q7mtvfg4p9gd8d0acx0hpc2mte9xqzthd7rd3348tsfhaesm",
    asset_type: AssetType.doge,
  },
];

export const TEMPORARY_DISABLED_CHAINS = [];

export const NON_MINTABLE_L2_CHAINS = [
  ChainName.Bitcoin,
  ChainName.Ethereum,
  ChainName.Ton,
  ChainName.Solana,
  ChainName.Osmosis,
  ChainName.Doge,
  ChainName.Sui,
  ChainName.Base,
];

export const NON_BURNABLE_L2_CHAINS = [
  ChainName.Bitcoin,
  ChainName.Ethereum,
  ChainName.Ton,
  ChainName.Solana,
  ChainName.Osmosis,
  ChainName.Doge,
  ChainName.Sui,
  ChainName.Base,
];

export const MIN_CKBTC_TRANSFER_AMOUNT = "0.0006";
export const MIN_CKBTC_REDEEM_AMOUNT = "0.0005";

export const OSMOSIS_ALLBTC_CONTRACT_ADDRESS =
  "osmo1z6r6qdknhgsc0zeracktgpcxf43j6sekq07nw8sxduc9lg0qjjlqfu25e3";

export const MIN_DOGE_TRANSFER_AMOUNT = "0.1";
